import {add} from 'date-fns';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {GetCustomerPaymentApiResponse} from '@dms/api/finance';
import {AftersalesPaymentMethod, Payment} from '@dms/api/sales';

import {parseDate} from 'shared/web';

import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';

interface BuildPaymentFormDefaultValuesParams {
  payment: Payment;
  customerPayment: GetCustomerPaymentApiResponse | undefined;
  defaultPaymentType?: AftersalesPaymentMethod;
  defaultBankAccountValue?: string;
}

export function buildPaymentFormDefaultValues(
  params: BuildPaymentFormDefaultValuesParams
): Partial<CheckoutPaymentFormType> {
  const {payment, customerPayment, defaultPaymentType, defaultBankAccountValue} = params;

  const defaultPaymentMethod = match<
    [AftersalesPaymentMethod | undefined, boolean],
    AftersalesPaymentMethod | undefined
  >([payment.method ?? defaultPaymentType, !!customerPayment?.allowBankTransfer])
    .with(['BANK_TRANSFER', false], always('CARD'))
    .otherwise(([paymentType]) => paymentType);

  // The backend fills due/dueDate on payment creation, so the customer invoice maturity
  // is only a fallback and applies to bank transfers alone
  const fallbackDue = match(defaultPaymentMethod)
    .with('BANK_TRANSFER', () => customerPayment?.invoiceMaturity ?? 0)
    .otherwise(always(0));

  const due = payment.due ?? fallbackDue;
  // A persisted issue date is the draft's creation date, not the date the document gets
  // issued, so the form always opens on today and re-anchors the due date on it
  const issueDate = new Date();

  return {
    amount: payment.paymentAmount?.amount ?? undefined,
    bankAccount: payment.bankAccount?.number ?? defaultBankAccountValue ?? undefined,
    cashRegisterId: payment.cashRegisterId ?? undefined,
    note: payment.note ?? undefined,
    method: defaultPaymentMethod,
    due,
    dueDate: add(issueDate, {days: due}),
    issueDate,
    dateOfTaxableSupply: issueDate,
    currency: payment.foreignCurrencyPaymentAmount?.currency,
    customerServiceContractId: payment.customerServiceContractId ?? undefined,
    templateId: payment.templateId ?? undefined,
    exchangeRate: payment.exchangeRate ? parseFloat(payment.exchangeRate) : undefined,
    correctionReason: payment.correctionDetails?.reason ?? undefined,
    dateOfReception: payment.correctionDetails?.dateOfReception
      ? parseDate(payment.correctionDetails.dateOfReception)
      : undefined,
  };
}
